<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import gettext from '@/i18n/gettext';
import type {
  CatalogueProjectBom,
  CatalogueProjectBomItem,
} from '@/models/Projects';
import { CatalogueProjectBomType } from '@/models/Projects';
import type { CatalogueItem, CatalogueItemGroup } from '@/models/Catalogue';
import { getBomItems } from '@/api/ProjectsApi';
import { getCatalogueItem, getCatalogueGroups } from '@/api/CatalogueApi';
import formatCost from '@/helpers/CurrencyHelper';
import BomGroupedItems from '@/components/projects/BomGroupedItems.vue';

const { $gettext } = gettext;

interface Props {
  modelValue: boolean;
  projectName: string;
  boms: CatalogueProjectBom[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const closeDialog = () => {
  emit('update:modelValue', false);
};

const sortedBoms = computed(() => {
  if (!props.boms) {
    return [] as CatalogueProjectBom[];
  }
  return [...props.boms].sort((a, b) => a.name.localeCompare(b.name));
});

// BOM selection
const selectedBomUuids = ref<string[]>([]);
const bomSelectMenuOpen = ref(false);

const bomSelectItems = computed(() =>
  sortedBoms.value.map((b) => ({ title: b.name, value: b.uuid })),
);

const selectedBoms = computed(() =>
  sortedBoms.value.filter((b) => selectedBomUuids.value.includes(b.uuid)),
);

const loading = ref(false);
const error = ref<string | null>(null);
const groups = ref<CatalogueItemGroup[]>([]);

type DetailedItem = {
  item: CatalogueItem;
  quantity: number;
  addedAt: Date;
  parentItemUuid?: string;
};

const itemsByBom = ref<Record<string, DetailedItem[]>>({});

// Total cost per BOM
const totalCostByBom = computed<Record<string, number>>(() => {
  const result: Record<string, number> = {};
  for (const [bomUuid, items] of Object.entries(itemsByBom.value)) {
    result[bomUuid] = items.reduce(
      (sum, di) => sum + di.item.cost * di.quantity,
      0,
    );
  }
  return result;
});

// Price difference: first selected BOM is baseline
const baselineBomUuid = computed(() => selectedBoms.value[0]?.uuid ?? null);

const costDifference = (bomUuid: string): number | null => {
  if (!baselineBomUuid.value || bomUuid === baselineBomUuid.value) {
    return null;
  }
  return (
    (totalCostByBom.value[bomUuid] ?? 0) -
    (totalCostByBom.value[baselineBomUuid.value] ?? 0)
  );
};

const costDifferenceColor = (diff: number): string => {
  if (diff < 0) {
    return 'success';
  }
  if (diff > 0) {
    return 'error';
  }
  return 'default';
};

const formatDifference = (diff: number): string => {
  const sign = diff > 0 ? '+' : '';
  return sign + formatCost(diff);
};

const loadData = async () => {
  if (!props.boms?.length) {
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    groups.value = await getCatalogueGroups();
    const entries = await Promise.all(
      props.boms.map(async (bom) => {
        const serverItems: CatalogueProjectBomItem[] = await getBomItems(
          bom.uuid,
        );
        const detailed: DetailedItem[] = [];
        await Promise.all(
          serverItems.map(async (s) => {
            try {
              const itemDetails = await getCatalogueItem(s.itemUuid);
              detailed.push({
                item: itemDetails,
                quantity: s.quantity,
                addedAt: new Date(s.createdAt),
                parentItemUuid: s.parentItemUuid,
              });
            } catch (e) {
              // best effort: skip item if lookup fails
              console.error(e);
            }
          }),
        );
        return [bom.uuid, detailed] as const;
      }),
    );
    itemsByBom.value = Object.fromEntries(entries);
  } catch (e) {
    console.error(e);
    error.value = $gettext('Failed to load BOM comparison data');
  } finally {
    loading.value = false;
  }
};

watch(selectedBomUuids, () => {
  bomSelectMenuOpen.value = false;
});

watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      selectedBomUuids.value = sortedBoms.value.map((b) => b.uuid);
      await loadData();
    }
  },
);

onMounted(async () => {
  if (props.modelValue) {
    selectedBomUuids.value = sortedBoms.value.map((b) => b.uuid);
    await loadData();
  }
});
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1200"
    @update:model-value="(val) => emit('update:modelValue', val)"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>{{ $gettext('Compare BOMs') }} — {{ projectName }}</span>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="closeDialog" />
      </v-card-title>

      <v-card-text>
        <div v-if="!boms || boms.length === 0" class="text-medium-emphasis">
          {{ $gettext('No BOMs in this project') }}
        </div>
        <v-alert v-else-if="error" type="error" variant="tonal" class="mb-4">
          {{ error }}
        </v-alert>
        <template v-else>
          <v-select
            v-model="selectedBomUuids"
            v-model:menu="bomSelectMenuOpen"
            :items="bomSelectItems"
            :label="$gettext('Select BOMs to compare')"
            multiple
            chips
            closable-chips
            variant="outlined"
            density="compact"
            class="mb-4"
          />
          <v-row v-if="selectedBoms.length >= 2">
            <v-col
              v-for="bom in selectedBoms"
              :key="bom.uuid"
              :cols="
                Math.max(3, Math.floor(12 / Math.min(selectedBoms.length, 4)))
              "
              class="pa-2"
            >
              <v-card variant="outlined" class="h-100 d-flex flex-column">
                <v-card-title class="text-subtitle-1 d-flex align-center">
                  <span>{{ bom.name }}</span>
                  <v-chip
                    size="small"
                    variant="flat"
                    :color="
                      bom.bomType === CatalogueProjectBomType.ebom
                        ? 'primary'
                        : 'secondary'
                    "
                    class="ml-2"
                  >
                    {{ bom.bomType.toUpperCase() }}
                  </v-chip>
                </v-card-title>
                <v-card-text class="flex-grow-1">
                  <div v-if="loading" class="text-medium-emphasis">
                    <v-progress-circular indeterminate color="primary" />
                  </div>
                  <BomGroupedItems
                    v-else
                    :items="itemsByBom[bom.uuid] || []"
                    :groups="groups"
                  />
                </v-card-text>
                <v-divider />
                <v-card-actions class="pa-4">
                  <div class="d-flex align-center w-100">
                    <span class="text-subtitle-2 font-weight-bold">
                      {{ $gettext('Total') }}:
                      {{ formatCost(totalCostByBom[bom.uuid] ?? 0) }}
                    </span>
                    <v-spacer />
                    <v-chip
                      v-if="bom.uuid === baselineBomUuid"
                      size="small"
                      variant="tonal"
                      color="info"
                    >
                      {{ $gettext('Baseline') }}
                    </v-chip>
                    <v-chip
                      v-else-if="costDifference(bom.uuid) !== null"
                      size="small"
                      variant="tonal"
                      :color="
                        costDifferenceColor(costDifference(bom.uuid) as number)
                      "
                    >
                      {{ formatDifference(costDifference(bom.uuid) as number) }}
                    </v-chip>
                  </div>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
          <div
            v-else-if="selectedBoms.length < 2"
            class="text-medium-emphasis text-center py-4"
          >
            {{ $gettext('Select at least 2 BOMs to compare') }}
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.v-card {
  border-radius: 8px;
}
</style>
